/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: {
        container: {},
        imageBackgroundOverlay: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundImage:
                'radial-gradient(45.54% 45.54% at 50% 50%, rgba(255, 255, 255, 0) 14.06%, rgba(155, 155, 155, 0.05) 100%)',
            display: 'block'
        },
        controls: {
            overflow: 'hidden',
            position: 'relative'
        },
        navButton: {
            variant: 'unstyled',
            position: 'absolute',
            top: '50%',
            visibility: {base: 'hidden', lg: 'visible'}
        },
        navButtonLeft: {
            left: 6
        },
        navButtonRight: {
            right: 6
        },
        scrollBar: {
            alignItems: 'stretch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                height: 0
            },
            touchAction: 'pan-y'
        },
        sliderButtons: {
            cursor: 'pointer'
        },
        imageAspectParent: {
            flex: '0 0 auto',
            width: 'full',
            maxHeight: 'full',
            scrollSnapAlign: 'start'
        },
        imageAspectChild: {
            position: 'relative',
            height: 'full',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        image: {
            height: 'full',
            imageProps: {
                width: 'full',
                maxHeight: 'full'
            }
        },
        hotspot: {
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            color: 'stokkeCore.orange'
        },
        thumbnailsContainer: {
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto'
        },
        thumbnailImageItem: {
            flexShrink: 0,
            cursor: 'pointer'
        }
    },
    variants: {
        cockpit: ({theme}) => {
            return {
                image: {
                    imageProps: {
                        objectFit: 'contain'
                    }
                },
                imageAspectChild: {
                    sx: {aspectRatio: '7/8'}
                },
                imageBackgroundOverlay: {
                    display: null,
                    /* copied from SG */
                    backgroundImage: theme.gradients.greyBackgroundOfProductImages
                },
                navButton: {
                    boxSize: 10,
                    // flex to center icon vertically and horizontally
                    display: {
                        base: 'none',
                        lg: 'flex'
                    },
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                overlay: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    paddingTop: {base: 3, lg: 6},
                    paddingX: {base: 4, lg: 6},
                    paddingBottom: {base: 4, lg: 6},
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    pointerEvents: 'none'
                },
                paginationItem: {
                    pointerEvents: 'auto'
                }
            }
        },
        gallery: {
            imageAspect: {
                ratio: 2
            },
            navButton: {
                display: {
                    base: 'none',
                    sm: 'block'
                }
            }
        },
        zoom: {
            container: {
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
            },
            imageAndOverlayContainer: {
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
            },
            imageBackgroundOverlay: {
                display: 'none'
            },
            imageContainer: {
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                backgroundColor: 'stokkeCore.white'
            },
            scrollBar: {
                flex: '1 1 auto'
            },
            imageAspectParent: {
                width: 'full',
                height: 'full'
            },
            imageAspect: {
                width: 'full',
                height: 'full'
            },
            imageAspectChild: {
                width: 'full',
                height: 'full'
            },
            image: {
                imageProps: {
                    width: 'full',
                    height: 'full',
                    objectFit: 'contain'
                }
            },
            navButton: {
                display: {
                    base: 'none',
                    xl: 'block'
                }
            },
            thumbnailsContainer: {
                // prevent shrinking on wide screens
                flex: '0 0 auto',
                marginX: {base: 2, xl: 6},
                paddingY: 6,
                gap: {base: 2, xl: 4}
            },
            thumbnailImageItem: {
                display: 'flex',
                alignItems: 'center',
                height: {
                    // same strange size from figma
                    base: '3.625rem',
                    xl: '6.125rem'
                }
            },
            thumbnailImageItemSelected: {
                border: '2px solid'
            },
            thumbnailImage: {
                height: '100%',
                maxHeight: '100%',
                width: 'auto',
                maxWidth: 'unset'
            }
        },
        setProduct: ({theme}) => {
            return {
                imageBackgroundOverlay: {
                    display: null,
                    backgroundImage: theme.gradients.psdpModalGreyBackgroundOfProductImages,
                    mixBlendMode: 'multiply'
                },
                overlay: {
                    display: {base: 'none', xl: 'flex'},
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '1rem',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'stretch',
                    pointerEvents: 'none'
                },
                controls: {
                    top: 0,
                    left: 0,
                    right: 0
                },
                navButton: {
                    display: {
                        base: 'none',
                        xl: 'flex'
                    },
                    position: 'absolute',
                    top: '50%'
                },
                navButtonLeft: {
                    left: '0.25rem'
                },
                navButtonRight: {
                    right: '0.25rem'
                },
                paginationItem: {
                    pointerEvents: 'auto'
                }
            }
        }
    },
    sizes: {
        sm: {
            heroImageSkeleton: {
                maxWidth: ['none', 'none', 'none', '500px']
            },
            heroImage: {
                maxWidth: ['none', 'none', 'none', '500px']
            }
        },
        md: {
            heroImageSkeleton: {
                maxWidth: ['none', 'none', 'none', '680px']
            },
            heroImage: {
                maxWidth: ['none', 'none', 'none', '680px']
            }
        }
    },
    defaultProps: {
        size: 'md'
    },
    parts: [
        'container',
        'heroImageGroup',
        'heroImage',
        'heroImageSkeleton',
        'thumbnailImageGroup',
        'imageBackgroundOverlay',
        'image',
        'imageAspectChild',
        'overlay',
        'controls',
        'scrollBar',
        'imageAspectParent',
        'imageAndOverlayContainer',
        'imageContainer',
        'thumbnailsContainer',
        'thumbnailImageItem',
        'thumbnailImage',
        'navButton',
        'navButtonLeft',
        'navButtonRight',
        'sliderButtons',
        'hotspot',
        'thumbnailImageItemSelected',
        'paginationItem',
        'wishlistToggleButton'
    ]
}
